import { Users } from "lucide-react";
import { useState } from "react";
import { useGetList, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import {
  LEAD_SOURCE_CHOICES,
  LIFECYCLE_STAGE_CHOICES,
} from "../../atomic-crm/contacts/crmEnrichmentModel";
import { SectionCard } from "../data-display/SectionCard";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import type { SegmentDefinition, SegmentPreviewRow } from "./campaignModel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface TagChoice {
  id: number;
  name: string;
}

const toggleValue = <T,>(values: T[] | undefined, value: T): T[] => {
  const current = values ?? [];
  return current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
};

const ChipToggle = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-2.5 py-1 text-[12px] transition-colors",
      active
        ? "border-primary bg-primary text-primary-foreground"
        : "border-border text-muted-foreground hover:border-primary/50",
    )}
  >
    {label}
  </button>
);

/**
 * Declarative audience filters: filters combine with AND, values within one
 * filter with OR; everything empty means "all contacts". Matching happens
 * server-side (private.segment_matches) so preview and send always agree.
 */
export const SegmentFilterFields = ({
  definition,
  onChange,
}: {
  definition: SegmentDefinition;
  onChange: (definition: SegmentDefinition) => void;
}) => {
  const { data: tags } = useGetList<TagChoice>("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <p className="text-[12.5px] font-medium">Lifecycle stage</p>
        <div className="flex flex-wrap gap-1.5">
          {LIFECYCLE_STAGE_CHOICES.map((choice) => (
            <ChipToggle
              key={choice.id}
              label={choice.name}
              active={(definition.lifecycle_stages ?? []).includes(choice.id)}
              onClick={() =>
                onChange({
                  ...definition,
                  lifecycle_stages: toggleValue(
                    definition.lifecycle_stages,
                    choice.id,
                  ),
                })
              }
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-[12.5px] font-medium">Lead source</p>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_SOURCE_CHOICES.map((choice) => (
            <ChipToggle
              key={choice.id}
              label={choice.name}
              active={(definition.sources ?? []).includes(choice.id)}
              onClick={() =>
                onChange({
                  ...definition,
                  sources: toggleValue(definition.sources, choice.id),
                })
              }
            />
          ))}
        </div>
      </div>

      {(tags ?? []).length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[12.5px] font-medium">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {(tags ?? []).map((tag) => (
              <ChipToggle
                key={tag.id}
                label={tag.name}
                active={(definition.tag_ids ?? []).includes(tag.id)}
                onClick={() =>
                  onChange({
                    ...definition,
                    tag_ids: toggleValue(definition.tag_ids, tag.id),
                  })
                }
              />
            ))}
          </div>
        </div>
      )}

      <label className="flex flex-col gap-1 text-[12.5px] font-medium">
        Cities (comma-separated)
        <input
          className={inputClass}
          placeholder="Denver, Boulder"
          value={(definition.cities ?? []).join(", ")}
          onChange={(e) =>
            onChange({
              ...definition,
              cities: e.target.value
                .split(",")
                .map((city) => city.trim())
                .filter(Boolean),
            })
          }
        />
      </label>
    </div>
  );
};

/**
 * Live audience preview through the RLS-scoped preview_segment RPC, * shows who matches and who a send would suppress (consent/DNC).
 */
export const AudiencePreview = ({
  definition,
  channel,
}: {
  definition: SegmentDefinition;
  channel: string;
}) => {
  const notify = useNotify();
  const [rows, setRows] = useState<SegmentPreviewRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  const preview = async () => {
    setLoading(true);
    const { data, error } = await getSupabaseClient().rpc("preview_segment", {
      p_workspace_id: getActiveWorkspaceId(),
      p_definition: definition,
      p_channel: channel,
    });
    setLoading(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    setRows((data ?? []) as SegmentPreviewRow[]);
  };

  const eligible = (rows ?? []).filter((r) => r.eligible).length;
  const blocked = (rows ?? []).length - eligible;

  return (
    <SectionCard
      icon={Users}
      title="Audience preview"
      subtitle="Blocked contacts are counted but never messaged"
    >
      <div className="mb-2 flex justify-start">
        <Button
          size="sm"
          variant="outline"
          disabled={loading}
          onClick={preview}
        >
          {loading ? "Loading…" : "Preview audience"}
        </Button>
      </div>
      {rows === null ? (
        <p className="py-2 text-[13px] text-muted-foreground">
          Run a preview to see who this audience matches.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-[13px]">
            <span className="font-semibold">{rows.length}</span> matching ·{" "}
            <span className="font-semibold text-emerald-700">{eligible}</span>{" "}
            reachable ·{" "}
            <span className="font-semibold text-amber-700">{blocked}</span>{" "}
            blocked by consent
          </p>
          <div className="max-h-48 overflow-y-auto">
            {rows.map((row) => (
              <div
                key={row.contact_id}
                className="flex items-center justify-between border-b border-border-subtle py-1.5 text-[12.5px] last:border-0"
              >
                <span className="truncate">
                  {row.first_name} {row.last_name}
                  {row.city ? ` · ${row.city}` : ""}
                </span>
                <span
                  className={cn(
                    "text-[11.5px]",
                    row.eligible ? "text-muted-foreground" : "text-amber-700",
                  )}
                >
                  {row.eligible
                    ? row.lifecycle_stage
                    : (row.block_reason ?? "blocked")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
};
